import { useCallback, useEffect, useRef, useState } from 'react';
import { webchatApi } from '../../services/api';
import { t } from '../../i18n';
import type { RateLimitLock, WidgetState } from '../../types';

interface UseRateLimitGateArgs {
  rateLimitLock: RateLimitLock | null;
  setState(partial: Partial<WidgetState>): void;
  requestInputFocus(): void;
  addAssistantMessage(content: string): void;
}

export function useRateLimitGate({
  rateLimitLock,
  setState,
  requestInputFocus,
  addAssistantMessage,
}: UseRateLimitGateArgs) {
  const [countdownTick, setCountdownTick] = useState(0);
  const captchaInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!rateLimitLock) return;
    const id = setInterval(() => setCountdownTick((tick) => tick + 1), 1000);
    return () => clearInterval(id);
  }, [rateLimitLock]);

  const remaining = rateLimitLock
    ? Math.max(0, Math.ceil((rateLimitLock.lockedUntil - Date.now()) / 1000))
    : 0;

  const fetchCaptchaChallenge = useCallback(async () => {
    try {
      const { token, question } = await webchatApi.captchaStart();
      setState({
        rateLimitLock: {
          lockedUntil: rateLimitLock?.lockedUntil || Date.now(),
          captchaRequired: true,
          captchaToken: token,
          captchaQuestion: question,
        },
      });
    } catch {
      setState({ rateLimitLock: null, isLoading: false });
      requestInputFocus();
    }
  }, [rateLimitLock?.lockedUntil, requestInputFocus, setState]);

  useEffect(() => {
    if (
      rateLimitLock &&
      rateLimitLock.captchaRequired &&
      !rateLimitLock.captchaToken &&
      Date.now() >= rateLimitLock.lockedUntil
    ) {
      void fetchCaptchaChallenge();
    }
  }, [countdownTick, fetchCaptchaChallenge, rateLimitLock]);

  const handleCaptchaSubmit = useCallback(async () => {
    const answer = parseInt(captchaInputRef.current?.value || '', 10);
    if (isNaN(answer) || !rateLimitLock?.captchaToken) return;

    try {
      const result = await webchatApi.captchaVerify(rateLimitLock.captchaToken, answer);
      if (result.success) {
        setState({ rateLimitLock: null });
        requestInputFocus();
      } else {
        if (captchaInputRef.current) captchaInputRef.current.value = '';
        addAssistantMessage(t('captchaFailed'));
        void fetchCaptchaChallenge();
      }
    } catch {
      addAssistantMessage(t('captchaFailed'));
      void fetchCaptchaChallenge();
    }
  }, [addAssistantMessage, fetchCaptchaChallenge, rateLimitLock?.captchaToken, requestInputFocus, setState]);

  useEffect(() => {
    if (rateLimitLock?.captchaQuestion) {
      captchaInputRef.current?.focus();
    }
  }, [rateLimitLock?.captchaQuestion]);

  return {
    remaining,
    captchaInputRef,
    handleCaptchaSubmit,
  };
}
